/**
 * De chatsamenstelling van één stelling, per leerjaar: welke persona's de
 * leerlingen mogen kiezen, hoeveel kennisrollen er verdeeld worden, en hoe
 * lang de chat en de afronding duren.
 *
 * De rekensom onderaan: de leerling kiest precies zoveel personages als er
 * kennisrollen zijn ingesteld, en dat moet passen in wat hier is
 * aangevinkt -- anders kan hij zijn keuze nooit rond krijgen. Kiest hij
 * daarbij een personage met een vaste rol (twijfelaar, echoput, afleider),
 * dan neemt dat een plek in en blijft er een kennisrol over; die valt dan
 * weg, en in de resultaten is te zien welke.
 */
import React, { useEffect, useState } from 'react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';

const LEERJAREN = [6, 7, 8];
const KENNISROLLEN = [['expert_pro', 'Expert PRO'], ['expert_contra', 'Expert CONTRA'], ['kennis_pro', 'Kennis PRO'], ['kennis_contra', 'Kennis CONTRA']];

const perLeerjaar = (maak) => Object.fromEntries(LEERJAREN.map(j => [j, maak()]));
const legeVerdeling = () => ({ expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 });
const legeInstelling = () => ({ agents: [], kennis_verdeling: 'pro_plus', tijdsduur_chat: 15, tijdsduur_evaluatie: 5 });

export function ChatSamenstelling({ api, stelling }) {
  const [leerjaar, setLeerjaar] = useState(6);
  const [instelling, setInstelling] = useState(perLeerjaar(legeInstelling));
  const [verdeling, setVerdeling] = useState(perLeerjaar(legeVerdeling));
  const [gekozenIds, setGekozenIds] = useState(perLeerjaar(() => []));
  const [personas, setPersonas] = useState([]);

  useEffect(() => {
    // Drie aanroepen, elk apart afgehandeld: mislukt er één, dan komen de
    // andere twee gewoon binnen.
    const haal = async (vraag, verwerk) => {
      try {
        const data = await vraag();
        if (data.success) verwerk(data.data);
      } catch (err) { console.error('Fout:', err); }
    };
    haal(() => api.haalSamenstelling(stelling.id), (d) => {
      setInstelling(d);
      // De agents-lijst van de server wordt hier een telling per rol.
      const nieuw = perLeerjaar(legeVerdeling);
      LEERJAREN.forEach(j => {
        (d[j]?.agents || []).forEach(agent => {
          if (agent.agent_rol && Object.prototype.hasOwnProperty.call(nieuw[j], agent.agent_rol)) {
            nieuw[j][agent.agent_rol] = agent.aantal || 0;
          }
        });
      });
      setVerdeling(nieuw);
    });
    haal(() => api.haalStellingPersonas(stelling.id), (d) => {
      // De server geeft {6: [], 7: [], 8: []}; een oudere versie gaf één lijst.
      setGekozenIds(d && typeof d === 'object' && !Array.isArray(d) ? d : { 6: d || [], 7: [], 8: [] });
    });
    haal(api.haalPersonas, setPersonas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stelling.id]);

  const huidigeIds = gekozenIds[leerjaar] || [];
  const huidigeVerdeling = verdeling[leerjaar] || legeVerdeling();
  const huidigeInstelling = instelling[leerjaar] || legeInstelling();

  const wissel = (personaId) => {
    const nieuw = huidigeIds.includes(personaId) ? huidigeIds.filter(id => id !== personaId) : [...huidigeIds, personaId];
    setGekozenIds({ ...gekozenIds, [leerjaar]: nieuw });
  };

  const persona = (id) => personas.find(p => p.id === id);
  const aantalRollen = KENNISROLLEN.reduce((som, [rol]) => som + (huidigeVerdeling[rol] || 0), 0);
  const aantalPersonas = huidigeIds.filter(id => persona(id)?.vast_type !== 'discussieleider').length;
  // Zoveel kiest de leerling er: het aantal kennisrollen. Zo staat het ook in
  // de backend (routers/leerling/sessie.py), en die bepaalt wat de les doet.
  const metVasteRol = huidigeIds.filter(id => persona(id)?.vast_type).length;
  const klopt = aantalRollen <= aantalPersonas && aantalRollen > 0;

  const slaOp = async () => {
    try {
      const agents = KENNISROLLEN.filter(([rol]) => huidigeVerdeling[rol] > 0).map(([rol]) => ({ agent_rol: rol, aantal: huidigeVerdeling[rol] }));
      // Eerst de gekozen persona's voor dit leerjaar, dan de rollen en tijden.
      await api.slaStellingPersonasOp(stelling.id, leerjaar, huidigeIds);
      const data = await api.slaSamenstellingOp(stelling.id, {
        leerjaar, agents,
        kennis_verdeling: huidigeInstelling.kennis_verdeling,
        tijdsduur_chat: huidigeInstelling.tijdsduur_chat,
        tijdsduur_evaluatie: huidigeInstelling.tijdsduur_evaluatie,
      });
      if (data.success) alert('Configuratie opgeslagen!');
    } catch (err) { console.error('Fout:', err); }
  };

  const zetTijd = (veld, waarde) => setInstelling({ ...instelling, [leerjaar]: { ...huidigeInstelling, [veld]: parseInt(waarde) } });

  return (
    <div style={styles.configSection}>
      <h3 style={styles.subheader}>Chat samenstelling per leerjaar</h3>
      <div style={{ height: '16px' }}></div>
      <div style={styles.leerjaarTabs}>
        {LEERJAREN.map(jaar => (
          <button key={jaar} onClick={() => setLeerjaar(jaar)} style={leerjaar === jaar ? styles.leerjaarTabActive : styles.leerjaarTab}>
            Leerjaar {jaar}
          </button>
        ))}
      </div>
      <div style={{ height: '24px' }}></div>

      <h4>Beschikbare persona's</h4>
      <p style={styles.beschrijving}>Selecteer welke persona's leerlingen kunnen kiezen.</p>
      <div style={{ height: '12px' }}></div>
      <div style={styles.personaSelectGrid}>
        {personas.filter(p => p.vast_type !== 'discussieleider').map(p => (
          <div key={p.id} onClick={() => wissel(p.id)} style={huidigeIds.includes(p.id) ? styles.personaSelectItemActive : styles.personaSelectItem}>
            <div style={styles.personaSelectName}>{p.naam}</div>
            <div style={styles.personaSelectType}>{p.vast_type || 'flexibel'}</div>
          </div>
        ))}
      </div>
      <div style={{ height: '8px' }}></div>
      <div style={styles.totalPersonas}>{huidigeIds.length} persona's geselecteerd voor leerjaar {leerjaar}</div>
      <div style={{ height: '24px' }}></div>

      <h4>Verdeling Kennisrollen</h4>
      <p style={styles.beschrijving}>Verdeel de kennisrollen over de geselecteerde persona's (excl. discussieleider, twijfelaar, etc.). Indien persona's zonder kennisrol worden geselecteerd, komen deze in de plaats van de kennisrollen. </p>
      <div style={{ height: '12px' }}></div>
      <div style={styles.rollenGrid}>
        {KENNISROLLEN.map(([rol, label]) => (
          <div key={rol} style={styles.rolInput}>
            <label>{label}</label>
            <input type="number" min="0" value={huidigeVerdeling[rol] || 0}
              onChange={(e) => setVerdeling({ ...verdeling, [leerjaar]: { ...huidigeVerdeling, [rol]: parseInt(e.target.value) || 0 } })}
              style={styles.tijdsduurInput} />
          </div>
        ))}
      </div>
      <div style={{ height: '8px' }}></div>
      <div style={{ ...styles.totalPersonas, background: klopt ? kleur.accent : kleur.rood, color: klopt ? 'black' : 'white' }}>
        Leerling kiest: {aantalRollen} persona's (uit {aantalPersonas} beschikbaar)
      </div>
      {klopt && metVasteRol > 0 && (
        <p style={styles.beschrijving}>
          Let op: {metVasteRol === 1 ? 'één van de aangevinkte personages heeft' : metVasteRol + ' aangevinkte personages hebben'}{' '}
          een vaste rol. Kiest een groepje {metVasteRol === 1 ? 'dat personage' : 'die personages'}, dan neemt
          dat een plek in en blijft er een kennisrol over. Die valt dan weg —
          bij de resultaten staat welke.
        </p>
      )}
      <div style={{ height: '24px' }}></div>

      <div style={styles.tijdsduurGrid}>
        <div>
          <h4>Tijdsduur chat (minuten)</h4>
          <div style={{ height: '8px' }}></div>
          <input type="number" value={huidigeInstelling.tijdsduur_chat || 15} onChange={(e) => zetTijd('tijdsduur_chat', e.target.value)} style={styles.tijdsduurInput} />
        </div>
        <div>
          <h4>Tijdsduur evaluatie (minuten)</h4>
          <div style={{ height: '8px' }}></div>
          <input type="number" value={huidigeInstelling.tijdsduur_evaluatie || 5} onChange={(e) => zetTijd('tijdsduur_evaluatie', e.target.value)} style={styles.tijdsduurInput} />
        </div>
      </div>
      <div style={{ height: '16px' }}></div>
      <button onClick={slaOp} disabled={!klopt}
        style={{ ...styles.saveBtn, opacity: klopt ? 1 : 0.5, cursor: klopt ? 'pointer' : 'not-allowed' }}>
        Configuratie opslaan
      </button>
    </div>
  );
}

const styles = {
  ...basis,
  configSection: { marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${kleur.lijn}` },
  leerjaarTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  leerjaarTab: { padding: '10px 20px', border: `1px solid ${kleur.lijn}`, background: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  leerjaarTabActive: { padding: '10px 20px', border: '2px solid black', background: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  personaSelectGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
  personaSelectItem: { padding: '12px', background: kleur.vlak, borderRadius: '8px', cursor: 'pointer', border: `1px solid ${kleur.lijn}` },
  personaSelectItemActive: { padding: '12px', background: kleur.accentLichter, borderRadius: '8px', cursor: 'pointer', border: '2px solid black' },
  personaSelectName: { fontWeight: 'bold', marginBottom: '4px' },
  personaSelectType: { fontSize: '12px', color: kleur.stil },
  totalPersonas: { padding: '12px', background: kleur.vlak, borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' },
  rollenGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
  rolInput: { display: 'flex', flexDirection: 'column', gap: '4px' },
  tijdsduurGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  tijdsduurInput: { width: '100px', padding: '10px', border: '1px solid black', borderRadius: '6px', fontSize: '16px' },
};
